import React, { useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  Snackbar,
  AppBar,
  Toolbar,
  CssBaseline,
  ThemeProvider,
} from '@mui/material';
import { LLMRequestDTO } from './types/api';
import { llmService } from './services/api';
import { useSnackbar } from './hooks/useSnackbar';
import { useFeatureList } from './hooks/useFeatureList';
import { APP_CONSTANTS, ERROR_MESSAGES } from './utils/constants';
import { isValidFeatureList } from './utils/helpers';
import { theme, appStyles } from './styles';
import PromptInput from './components/PromptInput';
import FeatureTable from './components/FeatureTable';

function App() {
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const { 
    featureList, 
    loading, 
    error, 
    setFeatureList, 
    setLoading, 
    setError, 
    updateFeatureDescription 
  } = useFeatureList();

  const checkServerHealth = useCallback(async () => {
    try {
      await llmService.checkHealth();
      showSnackbar(APP_CONSTANTS.SERVER_AVAILABLE_MESSAGE, 'success');
    } catch {
      showSnackbar(APP_CONSTANTS.SERVER_UNAVAILABLE_MESSAGE, 'error');
    }
  }, [showSnackbar]);

  useEffect(() => {
    checkServerHealth();
  }, [checkServerHealth]);

  const handleGenerateFeatures = async (request: LLMRequestDTO) => {
    setLoading(true);
    setError(null);

    try {
      const response = await llmService.generateFeatures(request);
      
      if (isValidFeatureList(response)) {
        setFeatureList(response);
        const totalFeatures = response.groups.reduce((total, group) => total + group.features.length, 0);
        showSnackbar(`Сгенерировано ${response.groups.length} групп с ${totalFeatures} фичами`, 'success');
      } else {
        throw new Error(ERROR_MESSAGES.INVALID_RESPONSE);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : ERROR_MESSAGES.UNKNOWN_ERROR;
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureRefined = (originalDescription: string, newDescription: string, feature: any) => {
    updateFeatureDescription(feature, newDescription);
    showSnackbar(APP_CONSTANTS.FEATURE_UPDATED_MESSAGE, 'success');
  };

  const handleCopySuccess = () => {
    showSnackbar(APP_CONSTANTS.COPY_SUCCESS_MESSAGE, 'success');
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={appStyles.appBarTitle}>
            AI BDM - Генератор фич-листов
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={appStyles.container}>
        <Typography variant="h4" component="h1" sx={appStyles.title}>
          Генератор фич-листов на основе AI
        </Typography>
        
        <Typography variant="body1" sx={appStyles.subtitle}>
          Введите промпт или требования для автоматической генерации фич-листа вашего проекта
        </Typography>

        <PromptInput
          onSubmit={handleGenerateFeatures}
          loading={loading}
          error={error || undefined}
        />

        {featureList && (
          <Box sx={appStyles.featureTableContainer}>
            <FeatureTable 
              featureList={featureList} 
              loading={loading}
              onCopySuccess={handleCopySuccess}
              onFeatureRefined={handleFeatureRefined}
              requirements={featureList.title}
            />
          </Box>
        )}
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={APP_CONSTANTS.SNACKBAR_DURATION}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default App; 